import { Fragment, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useForm, SubmitHandler, FormProvider } from "react-hook-form";
import { toast } from "react-hot-toast";

import { PlusSmallIcon } from "@heroicons/react/24/solid";

import Button from "src/common/buttons/Button";
import ButtonGroup from "src/common/buttons/ButtonGroup";
import TextInput from "src/common/forms/TextInput";
import DialogOverlay from "src/common/DialogOverlay";

import { CartItem } from "src/services/webservice";

interface AddFBAItemButtonProps {
  integrationConfigId: string | undefined;
  item: CartItem;
}

interface IFormInputs {
  quantity: number;
}

export function AddFBAItemButton({
  integrationConfigId,
  item,
}: AddFBAItemButtonProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const methods = useForm<IFormInputs>({
    defaultValues: {
      quantity: 0,
    },
  });

  const onSubmit: SubmitHandler<IFormInputs> = (data) => {
    setLoading(true);

    try {
      console.log(item.sku);
      // Get amazonSku from item.sku
      const amazonSku = item.sku;

      if (!amazonSku) {
        toast.error("Amazon SKU is required");
        setLoading(false);
        return;
      }

      // Get existing data from localStorage
      const storageKey = `fbaItems_${integrationConfigId}`;
      const existingData = localStorage.getItem(storageKey);

      let fbaData: {
        integrationConfigId: number;
        items: Array<{ amazonSku: string; quantity: number }>;
      };

      if (existingData) {
        fbaData = JSON.parse(existingData);
      } else {
        fbaData = {
          integrationConfigId: Number(integrationConfigId),
          items: [],
        };
      }

      // Find existing item with same amazonSku
      const existingItemIndex = fbaData.items.findIndex(
        (item) => item.amazonSku === amazonSku
      );

      if (existingItemIndex !== -1) {
        // Update existing item's quantity
        fbaData.items[existingItemIndex].quantity = data.quantity;
      } else {
        // Add new item
        fbaData.items.push({
          amazonSku: amazonSku,
          quantity: data.quantity,
        });
      }

      // Save back to localStorage
      localStorage.setItem(storageKey, JSON.stringify(fbaData));

      setIsOpen(false);
      methods.reset();
    } catch (error) {
      toast.error("Failed to save FBA item");
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setIsOpen(false);
  };
  const openModal = () => {
    setIsOpen(true);
  };

  if (integrationConfigId) {
    return (
      <>
        <Button
          label="Add FBA Item"
          design="secondary"
          icon={<PlusSmallIcon className="mr-2 h-6 w-6" />}
          func={openModal}
        />
        <Transition appear show={isOpen} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-10"
            open={isOpen}
            onClose={closeModal}
          >
            <DialogOverlay />
            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4 text-center">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-medium leading-6 text-gray-900"
                    >
                      Add FBA Item to Order
                      <p className="text-sm font-light text-gray-500 mt-2">
                        This will build a set of items and validate them with
                        Amazon to begin the FBA process in our warehouse.
                      </p>
                    </Dialog.Title>
                    <div className="mt-2">
                      <FormProvider {...methods}>
                        <form
                          onSubmit={methods.handleSubmit(onSubmit)}
                          className="space-y-4"
                        >
                          <TextInput
                            label="Quantity"
                            name="quantity"
                            type="number"
                            placeholder="13.00"
                            autoComplete="off"
                          />
                          <ButtonGroup>
                            <Button
                              label="Cancel"
                              design="secondary"
                              func={closeModal}
                            />
                            <Button
                              label={item ? "Update" : "Add"}
                              icon={
                                !item && (
                                  <PlusSmallIcon className="h-5 w-5 mr-2" />
                                )
                              }
                              type="submit"
                              loading={loading}
                            />
                          </ButtonGroup>
                        </form>
                      </FormProvider>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      </>
    );
  } else {
    return null;
  }
}
